import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Col, Row } from 'antd';
import { Form } from '@ant-design/compatible';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import Input from 'common/components/input';
import Modal from 'common/components/modal';
import MyMap from '~components/my-map';
import useStyles from '../../style';

const CITY_BELONG_CHINA = ['HK', 'TW', 'MO'];
const FOUR_MUNICIPALITY = ['北京市', '上海市', '天津市', '重庆市'];

const EditBillingInfoModal = ({
  visible,
  onCancel,
  form,
  landlord,
  updateLandlord,
  getLandlord,
}) => {
  const { getFieldDecorator, setFieldsValue, validateFields, resetFields } = form;

  const { t } = useTranslation();
  const classes = useStyles();

  const [loading, setLoading] = useState(false);

  const billingCityInput = useRef(null);
  const billingAddressInput = useRef(null);

  const SearchGoogleMap = useCallback(() => {
    if (billingCityInput?.current?.input) {
      const request = {
        types: ['(cities)'],
      };
      const searchCityService = new window.google.maps.places.Autocomplete(
        billingCityInput.current.input,
        request,
      );
      searchCityService.addListener('place_changed', () => {
        const { address_components } = searchCityService.getPlace();

        let country = null;
        let city = null;

        address_components.forEach(address => {
          if (address.types.includes('country') && CITY_BELONG_CHINA.includes(address.short_name)) {
            country = 'China';
            city = address.long_name;
          } else {
            if (address.types.includes('country')) {
              country = address.long_name;
            }

            if (
              ['postal_town', 'locality'].some(item => address.types.includes(item)) ||
              FOUR_MUNICIPALITY.includes(address.long_name)
            ) {
              city = address.long_name;
            }
          }
        });

        setFieldsValue({
          billingCity: `${city}, ${country}`,
        });
      });
    }
    if (billingAddressInput?.current?.input) {
      const request = {
        types: ['address'],
      };
      const searchAddressService = new window.google.maps.places.Autocomplete(
        billingAddressInput.current.input,
        request,
      );
      searchAddressService.addListener('place_changed', () => {
        const place = searchAddressService.getPlace();

        const postalCodeComponent = place.address_components.find(item =>
          item.types.includes('postal_code'),
        );

        setFieldsValue({
          billingAddress: place.formatted_address,
          billingPostalCode: postalCodeComponent ? postalCodeComponent.long_name : '',
        });
      });
    }
  }, [setFieldsValue]);

  const handleCancel = () => {
    onCancel();
    resetFields(['billingAddress', 'billingCity', 'billingPostalCode']);
  };

  const onOk = () => {
    validateFields((error, values) => {
      if (!error) {
        const { billingAddress, billingCity, billingPostalCode } = values;

        const cityResult = billingCity.split(',');

        setLoading(true);
        updateLandlord(
          {
            id: landlord.id,
            billingAddress,
            billingCity: cityResult?.[0],
            billingCountry: cityResult?.[1]?.replace(/^\s/, ''),
            billingPostalCode,
          },
          () => {
            setLoading(false);
            onCancel();
            getLandlord();
          },
          () => {
            setLoading(false);
          },
        );
      }
    });
  };

  useEffect(() => {
    if (visible) {
      document.addEventListener('googleApiMounted', SearchGoogleMap);
    } else {
      document.removeEventListener('googleApiMounted', SearchGoogleMap);
    }
  }, [SearchGoogleMap, visible]);

  return (
    <>
      <Modal
        title={t('cms.landlord.modal.title.edit_billing_info')}
        width={454}
        visible={visible}
        okText={t('cms.properties.edit.leave_alert.title')}
        cancelText={t('cms.landlord.modal.cancel.button')}
        wrapClassName={classes.modalWrapper}
        onCancel={handleCancel}
        onOk={onOk}
        cancelButtonProps={{
          className: 'ant-btn-cancel',
        }}
        okButtonProps={{
          loading,
          className: 'ant-btn-confirm',
        }}
      >
        <Form className={classes.formWrapper}>
          <Form.Item label={t('cms.landlord.detail.billing_city.label')}>
            {getFieldDecorator('billingCity', {
              rules: [
                {
                  required: true,
                  message: t('cms.landlord.modal.error.blank'),
                },
              ],
              initialValue: `${landlord?.billingCity?.name}, ${landlord?.billingCountry?.name}`,
              validateTrigger: 'onBlur',
            })(
              <Input
                ref={billingCityInput}
                placeholder={t('cms.landlord.modal.billing_city.placeholder')}
              />,
            )}
          </Form.Item>

          <Form.Item label={t('cms.landlord.detail.billing_address.label')} colon={false}>
            {getFieldDecorator('billingAddress', {
              rules: [
                {
                  required: true,
                  message: t('cms.landlord.modal.error.blank'),
                },
              ],
              initialValue: landlord?.billingAddress,
              validateTrigger: 'onBlur',
            })(
              <Input
                ref={billingAddressInput}
                placeholder={t('cms.landlord.modal.billing_address.placeholder')}
              />,
            )}
          </Form.Item>

          <Form.Item label={t('cms.landlord.detail.billing_zip_code.label')}>
            {getFieldDecorator('billingPostalCode', {
              rules: [
                {
                  max: 10,
                  message: t('cms.landlord.modal.error.billing_postal_code'),
                },
                {
                  required: true,
                  message: t('cms.landlord.modal.error.blank'),
                },
              ],
              initialValue: landlord?.billingPostalCode,
              validateTrigger: 'onBlur',
            })(<Input placeholder={t('cms.landlord.modal.billing_zip_code.placeholder')} />)}
          </Form.Item>
        </Form>
        <Row>
          <Col span={24} style={{ display: 'none' }}>
            <MyMap
              t={t}
              lastSearched={{
                lat: 0,
                lng: 0,
              }}
              form={form}
              value={{
                action: 'default',
                value: { lat: 0, lng: 0 },
              }}
            />
          </Col>
        </Row>
      </Modal>
    </>
  );
};
EditBillingInfoModal.propTypes = {
  form: PropTypes.object.isRequired,
  landlord: PropTypes.object.isRequired,
  getLandlord: PropTypes.func.isRequired,
  updateLandlord: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
};
export default Form.create()(EditBillingInfoModal);
